/**
 * Auto gratuity (ENG-5810): a gratuity the register adds on its own when a check meets a
 * rule the operator wrote — "parties of 6 or more pay 18%", "checks over $200 pay 20%",
 * "private room, $40". Several rules can apply to one check; each prints as its own line
 * under the operator's name for it. The amount is a tip: it never enters a tax base and
 * every tip figure in reporting already includes it.
 *
 * Backend: /backend/v2/auto_gratuity/ (app `auto_gratuity`, docs/auto-gratuity/README.md).
 * The engine rules R1–R6 live in auto_gratuity/engine.py; `computeAutoGratuities` below
 * mirrors R1–R3 and the override handling so the POS prices a check the same way locally.
 */

export type AutoGratuityAmountType = 'PERCENT' | 'FIXED';
export type AutoGratuityBasis = 'SUBTOTAL' | 'NET';
export type AutoGratuityMatch = 'ALL' | 'ANY';
export type AutoGratuityStatus = 'AUTO' | 'MANUAL' | 'WAIVED';

/** Occasion codes the rules are limited to (`BotOrder.occasion`). */
export const AUTO_GRATUITY_OCCASIONS: { code: number; name: string }[] = [
  { code: 1, name: 'Pickup' },
  { code: 2, name: 'Dine in' },
  { code: 3, name: 'Delivery' },
  { code: 4, name: 'Curbside' },
  { code: 5, name: 'Tableside' },
  { code: 6, name: 'To go' },
  { code: 7, name: 'For here' },
  { code: 8, name: 'Drive thru' }
];

/** A rule as the settings endpoints return it (GET store/<id>/, add, update) and as `store.auto_gratuity_rules` carries it. */
export interface AutoGratuityRule {
  id: number;
  name: string;
  /** Receipt label. Blank means "<name> <rate>%" (fixed rules: the name) is printed. */
  receipt_label?: string;
  /** The resolved receipt label, when the server sends it. */
  label?: string;
  amount_type: AutoGratuityAmountType;
  /** Decimal string with up to four places, e.g. "18.0000"; null for fixed rules. */
  rate_percent: string | number | null;
  /** Fixed amount, e.g. "40.00"; null for percent rules. */
  fixed_amount: string | number | null;
  basis: AutoGratuityBasis;
  min_guests: number | null;
  min_subtotal: string | number | null;
  match: AutoGratuityMatch;
  occasions: number[];
  apply_to_pos: boolean;
  apply_to_online: boolean;
  is_active?: boolean;
  sort_order?: number;
}

/** Body of add/ and update/ (update is partial). */
export interface AutoGratuityRuleInput {
  name: string;
  receipt_label?: string;
  amount_type: AutoGratuityAmountType;
  rate_percent?: string | number | null;
  fixed_amount?: string | number | null;
  basis?: AutoGratuityBasis;
  min_guests?: number | null;
  min_subtotal?: string | number | null;
  match?: AutoGratuityMatch;
  occasions?: number[];
  apply_to_pos?: boolean;
  apply_to_online?: boolean;
  is_active?: boolean;
  sort_order?: number;
}

/** GET store/<id>/ */
export interface AutoGratuityRuleList {
  rules: AutoGratuityRule[];
  occasions: { code: number; name: string }[];
}

/** One line of `payment.auto_gratuities` on an order (label, rate and base are snapshots). */
export interface OrderAutoGratuity {
  id: number;
  name: string;
  label: string;
  amount_type: AutoGratuityAmountType;
  rate_percent: string | number | null;
  fixed_amount: string | number | null;
  basis: AutoGratuityBasis;
  base: string | number;
  amount: string | number;
  status: 'AUTO' | 'MANUAL';
  guest_count: number | null;
}

/** One entry of `payment.auto_gratuities_waived`: a rule that fired but a cashier removed on this check. */
export interface WaivedAutoGratuity {
  id: number;
  name: string;
  label: string;
}

/** GET order/<hash>/ and the block a POST override answers with (plus `order`). */
export interface OrderAutoGratuityBlock {
  auto_gratuity: string | number;
  auto_gratuities: OrderAutoGratuity[];
  auto_gratuities_waived: WaivedAutoGratuity[];
  tip: string | number;
  total: string | number;
  due_total: string | number;
  cash_due_total: string | number;
  guest_count: number | null;
  rules: AutoGratuityRule[];
  /** The full order payload after an override (POST only). */
  order?: any;
}

/** One row of GET store/<id>/report/ `rules[]`. */
export interface AutoGratuityReportRule {
  id: number;
  name: string;
  label: string;
  amount_type: AutoGratuityAmountType | null;
  rate_percent: string | null;
  fixed_amount: number | null;
  order_count: number;
  base: number;
  amount: number;
  avg_per_order: number;
  cash_amount: number;
  card_amount: number;
  pos_amount: number;
  online_amount: number;
  manual_count: number;
  waived_count: number;
}

export interface AutoGratuityReport {
  rules: AutoGratuityReportRule[];
  by_server: { user_id: number | null; name: string; amount: number; order_count: number }[];
  by_day: { date: string; amount: number; order_count: number }[];
  totals: { amount: number; order_count: number; avg_per_order: number; waived_count: number; manual_count: number };
  time?: any;
}

/** "18.0000" -> "18", "17.5000" -> "17.5", 20 -> "20". */
export function formatGratuityRate(rate: string | number | null | undefined): string {
  if (rate === null || rate === undefined || `${rate}`.trim() === '') { return '0'; }
  const n = typeof rate === 'number' ? rate : parseFloat(rate);
  if (!isFinite(n)) { return '0'; }
  return n.toFixed(4).replace(/\.?0+$/, '');
}

/** The receipt label a rule prints with: its own label, else "<name> <rate>%" (fixed rules: the name). */
export function autoGratuityLabel(rule: {
  name: string; receipt_label?: string | null; amount_type?: AutoGratuityAmountType; rate_percent?: string | number | null;
}): string {
  if (rule.receipt_label && `${rule.receipt_label}`.trim()) { return `${rule.receipt_label}`.trim(); }
  if (rule.amount_type === 'FIXED') { return rule.name; }
  return `${rule.name} ${formatGratuityRate(rule.rate_percent)}%`;
}

/** "6+ guests", "$200+ checks", "6+ guests or $200+ checks" — the rule's trigger in words. */
export function autoGratuityTrigger(rule: Pick<AutoGratuityRule, 'min_guests' | 'min_subtotal' | 'match'>): string {
  const parts: string[] = [];
  if (rule.min_guests !== null && rule.min_guests !== undefined && `${rule.min_guests}` !== '') {
    parts.push(`${rule.min_guests}+ guests`);
  }
  if (rule.min_subtotal !== null && rule.min_subtotal !== undefined && `${rule.min_subtotal}` !== '') {
    parts.push(`$${formatMoney(rule.min_subtotal)}+ checks`);
  }
  return parts.join(rule.match === 'ANY' ? ' or ' : ' and ');
}

/** "18%" or "$40.00" — the rule's amount in words. */
export function autoGratuityAmountText(rule: Pick<AutoGratuityRule, 'amount_type' | 'rate_percent' | 'fixed_amount'>): string {
  return rule.amount_type === 'FIXED'
    ? `$${formatMoney(rule.fixed_amount)}`
    : `${formatGratuityRate(rule.rate_percent)}%`;
}

function formatMoney(value: string | number | null | undefined): string {
  const n = typeof value === 'number' ? value : parseFloat(`${value}`);
  if (!isFinite(n)) { return '0.00'; }
  return n.toFixed(2).replace(/\.00$/, '');
}

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

/** The facts R1 is decided on. */
export interface AutoGratuityFacts {
  /** Party size: the guest count the server entered, else the seats in use; null when unknown. */
  guestCount: number | null;
  /** Subtotal before discounts. */
  subTotal: number;
  /** `BotOrder.occasion` code (1 pickup … 8 drive thru), null when unknown. */
  occasion: number | null;
  channel: 'pos' | 'online' | 'kiosk' | 'other';
}

/** The console gets `bot_order.occasion` as a name ("DINE IN"); the rules hold codes. */
const OCCASION_CODES: { [name: string]: number } = {
  PICKUP: 1, 'DINE IN': 2, DINEIN: 2, DELIVERY: 3, CURBSIDE: 4, TABLESIDE: 5,
  'TO GO': 6, TOGO: 6, 'FOR HERE': 7, FORHERE: 7, 'DRIVE THRU': 8, DRIVETHRU: 8, 'DRIVE-THRU': 8
};

/** Occasion code from `bot_order.occasion` / `occasion_raw`, whichever is numeric or a known name. */
export function occasionCodeOf(botOrder: { occasion?: any; occasion_raw?: any } | null | undefined): number | null {
  if (!botOrder) { return null; }
  for (const raw of [botOrder.occasion_raw, botOrder.occasion]) {
    if (raw === null || raw === undefined || raw === '') { continue; }
    if (typeof raw === 'number' && isFinite(raw)) { return raw; }
    const text = `${raw}`.trim().toUpperCase();
    if (/^\d+$/.test(text)) { return +text; }
    const code = OCCASION_CODES[text] ?? OCCASION_CODES[text.replace(/[\s_-]+/g, ' ')];
    if (code) { return code; }
  }
  return null;
}

/** Mirrors engine.channel_of: kiosk, else POS, else online, else POS. */
export function channelOf(botOrder: { is_pos?: any; is_kiosk?: any; is_online?: any } | null | undefined): AutoGratuityFacts['channel'] {
  if (!botOrder) { return 'pos'; }
  if (botOrder.is_kiosk) { return 'kiosk'; }
  if (botOrder.is_pos) { return 'pos'; }
  if (botOrder.is_online) { return 'online'; }
  return 'pos';
}

/** Mirrors engine.excluded: gift card, kiosk, third-party, marketplace and KitchenHub orders never get one. */
export function autoGratuityExcluded(botOrder: any): boolean {
  if (!botOrder) { return false; }
  if (botOrder.is_gift_card_order || botOrder.is_kiosk) { return true; }
  if (+botOrder.payment_type === 9) { return true; }
  const source = `${botOrder.source || ''}`.trim().toUpperCase().replace(/[\s-]/g, '_');
  const raw = `${botOrder.payment_type_raw || ''}`.trim().toLowerCase().replace(/[\s-]/g, '_');
  return source === 'EXTERNAL_MARKETPLACE' || source === 'KITCHENHUB' || raw.startsWith('third_party');
}

/** "2,5" -> [2, 5]; accepts a list too; drops the table (0) and junk. Mirrors order.seats.parse_shares. */
export function parseSeatShares(value: any): number[] {
  if (value === null || value === undefined || value === '') { return []; }
  const raw: any[] = Array.isArray(value) ? value : `${value}`.split(',');
  const seats: number[] = [];
  for (const x of raw) {
    const n = parseInt(`${x}`.trim(), 10);
    if (isFinite(n) && n > 0 && !seats.includes(n)) { seats.push(n); }
  }
  return seats;
}

/** Distinct seats named on the order's lines (table lines name none). Mirrors engine.seats_in_use. */
export function seatsInUse(lines: any[]): number {
  const seats = new Set<number>();
  for (const line of lines || []) {
    if (!line || line.is_removed) { continue; }
    for (const s of parseSeatShares(line.seat_shares)) { seats.add(s); }
    const seat = parseInt(`${line.seat}`, 10);
    if (isFinite(seat) && seat > 0) { seats.add(seat); }
  }
  return seats.size;
}

/** Party size: the guest count the server entered, else the seats in use; null when the order says nothing. */
export function guestCountFor(botOrder: any, items: any[], miscItems: any[] = []): number | null {
  const entered = parseInt(`${botOrder?.bot_order_data?.guest_count ?? 0}`, 10);
  if (isFinite(entered) && entered > 0) { return entered; }
  const used = seatsInUse([...(items || []), ...(miscItems || [])]);
  return used > 0 ? used : null;
}

/** R1: does the rule fire for these facts? Mirrors engine.matches. */
export function ruleMatches(rule: AutoGratuityRule, facts: AutoGratuityFacts): boolean {
  if (facts.channel === 'pos' && !rule.apply_to_pos) { return false; }
  if (facts.channel === 'online' && !rule.apply_to_online) { return false; }
  if (facts.channel !== 'pos' && facts.channel !== 'online') { return false; }
  const occasions = (rule.occasions || []).map(o => +o).filter(o => isFinite(o));
  if (occasions.length && (facts.occasion === null || !occasions.includes(facts.occasion))) { return false; }
  const checks: boolean[] = [];
  if (rule.min_guests !== null && rule.min_guests !== undefined && `${rule.min_guests}` !== '') {
    checks.push(facts.guestCount !== null && facts.guestCount !== undefined && +facts.guestCount >= +rule.min_guests);
  }
  if (rule.min_subtotal !== null && rule.min_subtotal !== undefined && `${rule.min_subtotal}` !== '') {
    checks.push(round2(facts.subTotal) >= round2(+rule.min_subtotal));
  }
  if (!checks.length) { return false; }
  return rule.match === 'ANY' ? checks.some(c => c) : checks.every(c => c);
}

/** R3: `{base, amount}` for a rule on these figures. Mirrors engine.amount_for. */
export function ruleAmount(rule: AutoGratuityRule, subTotal: number, netSale: number): { base: number; amount: number } {
  if (rule.amount_type === 'FIXED') {
    return { base: round2(subTotal), amount: round2(+rule.fixed_amount || 0) };
  }
  const base = round2(rule.basis === 'NET' ? netSale : subTotal);
  if (base <= 0) { return { base, amount: 0 }; }
  const rate = (+rule.rate_percent || 0) / 100;
  return { base, amount: round2(base * rate) };
}

export interface AutoGratuityResult {
  lines: OrderAutoGratuity[];
  waived: WaivedAutoGratuity[];
  total: number;
  guestCount: number | null;
}

/**
 * Auto-gratuity lines for an order being priced locally — the same shape the backend's
 * `payment` dict carries, so every breakdown template reads either without caring which
 * computed it.
 *
 *  - R1  a rule fires when its channel and occasion allow it and its triggers hold
 *        (party size and/or check subtotal, combined by `match`)
 *  - R2  every rule that fires applies (they stack); a cashier's overrides survive:
 *        rule ids in `previousPayment.auto_gratuities_waived` are dropped (and listed as
 *        waived when they still fire), lines with `status === 'MANUAL'` in
 *        `previousPayment.auto_gratuities` are kept even when the rule does not fire
 *  - R3  percent rules take the subtotal before discounts (default) or after; fixed rules
 *        add the amount; a line with a zero base is dropped
 *  - R5  computed on menu prices, so cash and card get the same figure
 */
export function computeAutoGratuities(
  rules: AutoGratuityRule[] | null | undefined,
  botOrder: any,
  items: any[],
  miscItems: any[],
  subTotal: number,
  netSale: number,
  previousPayment?: { auto_gratuities?: OrderAutoGratuity[]; auto_gratuities_waived?: WaivedAutoGratuity[] } | null
): AutoGratuityResult {
  const empty: AutoGratuityResult = { lines: [], waived: [], total: 0, guestCount: null };
  const active = (rules || []).filter(r => r && r.is_active !== false && hasTrigger(r));
  if (!active.length || autoGratuityExcluded(botOrder)) { return empty; }

  const waivedIds = new Set<number>((previousPayment?.auto_gratuities_waived || []).map(w => +w.id));
  const manualIds = new Set<number>(
    (previousPayment?.auto_gratuities || []).filter(l => l && l.status === 'MANUAL').map(l => +l.id)
  );
  const needsGuests = active.some(r => r.min_guests !== null && r.min_guests !== undefined && `${r.min_guests}` !== '');
  const guestCount = needsGuests ? guestCountFor(botOrder, items, miscItems) : null;
  const facts: AutoGratuityFacts = {
    guestCount, subTotal, occasion: occasionCodeOf(botOrder), channel: channelOf(botOrder)
  };

  const lines: OrderAutoGratuity[] = [];
  const waived: WaivedAutoGratuity[] = [];
  let total = 0;
  for (const rule of active) {
    const fired = ruleMatches(rule, facts);
    const label = rule.label || autoGratuityLabel(rule);
    if (waivedIds.has(+rule.id)) {
      if (fired) { waived.push({ id: rule.id, name: rule.name, label }); }
      continue;
    }
    const manual = manualIds.has(+rule.id);
    if (!fired && !manual) { continue; }
    const { base, amount } = ruleAmount(rule, subTotal, netSale);
    if (amount <= 0) { continue; }
    total += amount;
    lines.push({
      id: rule.id,
      name: rule.name,
      label,
      amount_type: rule.amount_type,
      rate_percent: rule.amount_type === 'PERCENT' ? formatGratuityRate(rule.rate_percent) : null,
      fixed_amount: rule.fixed_amount !== null && rule.fixed_amount !== undefined ? (+rule.fixed_amount).toFixed(2) : null,
      basis: rule.basis || 'SUBTOTAL',
      base,
      amount,
      status: manual ? 'MANUAL' : 'AUTO',
      guest_count: guestCount
    });
  }
  return { lines, waived, total: round2(total), guestCount };
}

function hasTrigger(rule: AutoGratuityRule): boolean {
  const guests = rule.min_guests !== null && rule.min_guests !== undefined && `${rule.min_guests}` !== '';
  const check = rule.min_subtotal !== null && rule.min_subtotal !== undefined && `${rule.min_subtotal}` !== '';
  return guests || check;
}
